import { createSelector } from '@reduxjs/toolkit';
import { urls } from '@ff-os/shared-utils/urls';
import { selectAllPapers } from '@ff-scene-plugins/photo-prints-redux/selectors/paperSelectors';
import { selectAllVariants, selectVariantById } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { CompatibleWithRule, VariantOptionWithFixedPrice } from './VariantOption';

const selectPaperIdsForSize = createSelector([
    selectAllVariants,
    (state: PhotoPrintsPdpState, sizeId: string | undefined) => sizeId
], (variants, sizeId) => {
    return variants
        .filter(v => sizeId === undefined || v.sizeId === sizeId)
        .map(v => v.paperId);
});

const selectCompatibleSizeIdsByPaperId = createSelector([
    selectAllPapers,
    selectAllVariants
], (papers, variants) => {
    const compatibleSizeIdsByPaperId = {} as Record<string, CompatibleWithRule[]>;
    for (const paper of papers) {
        compatibleSizeIdsByPaperId[paper.id] = variants
            .filter(v => v.paperId === paper.id)
            .map(v => ({sizeId: v.sizeId}));
    }
    return compatibleSizeIdsByPaperId;
});

export const selectPapersOptionsForSize = createSelector([
    selectAllPapers, selectPaperIdsForSize, selectCompatibleSizeIdsByPaperId
], (allPapers, availablePaperIds, compatibleSizeIdsByPaperId) => {
    return allPapers.map(p => toVariantOptions(p, availablePaperIds, compatibleSizeIdsByPaperId));
});

export const selectCompatiblePaperOptions = createSelector([
    selectPapersOptionsForSize
], paperOptions => {
    return paperOptions.filter(p => p.available).length > 0
        ? paperOptions.filter(p => p.available)
        : paperOptions;
});

const selectSizeIdByVariantId = createSelector([
    selectVariantById
], variant => variant!.sizeId);

const selectPapersOptionsByVariantId = createSelector([
    state => state,
    selectSizeIdByVariantId
], selectPapersOptionsForSize);

export const selectActivePaperOptionForVariantId = createSelector([
    selectVariantById,
    selectPapersOptionsByVariantId
], (variant, papers) => papers.find(p => p.value === variant!.paperId)!);

export const selectDefaultPaperId = createSelector([
    selectAllPapers
], papers => papers.find(p => p.default)!.id);

const toVariantOptions = (
    paper: PhotoPrintsJson.Paper,
    availablePaperIds: string[],
    compatibleSizeIdsByPaperId: Record<string, CompatibleWithRule[]>
): VariantOptionWithFixedPrice => ({
    value: paper.id,
    name: { key: paper.name },
    iconUrl: urls.publicAssetUrl(paper.iconAssetId),
    compatibleWith: compatibleSizeIdsByPaperId[paper.id],
    available: availablePaperIds.includes(paper.id),
    price: paper.price,
    priceRequest: undefined,
    default: paper.default
});